import { m, type Message } from '@/i18n';
import { errOf, ok, type Result } from './types';

/**
 * `TimeValue`: one time type that carries its own **granularity** (DESIGN.md
 * "Time-Modul", §265). A value is a grain plus the interval it denotes, authored as a
 * single canonical string. The grain is *only* representation (how precisely the time
 * is expressed); it carries no tolerance and no severity. The engine reports one bare
 * **fact**: is `now` past the interval? That is `late`. What `late` *means* (the
 * consequence) is decided elsewhere, never inferred here (see docs/RECONCILIATION.md).
 *
 * Pure: no clock of its own. `now` is always passed in. Grain math is done in UTC, so
 * day/week/quarter targets are timezone-agnostic, and a minute-grain value carries its
 * own offset in the ISO string (`Z` or `+HH:MM`): a time of day without one names no
 * instant, so it is refused (`itemErrors.time.noOffset`), and the free-text input adds
 * the device's own offset before it commits (`withLocalOffset`). Every grain is read by
 * explicit arithmetic, never by the engine's date parser, so one stored value names one
 * interval on every device, and the listing statement computes the same end in SQL
 * (`time_value_end`, drizzle/0113_time_value_end.sql). Stored canonical so a re-parse is
 * stable.
 *
 * Architected-for, not built: minute/second precision beyond the instant, named grains
 * (sprint), and the renderer working in the coarser of (value grain, zoom) (§302).
 */

export type Grain = 'minute' | 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface TimeValue {
  grain: Grain;
  /** half-open interval [startMs, endMs) in epoch ms (UTC). */
  startMs: number;
  endMs: number;
  /** the canonical serialization (what we store and re-parse). */
  canonical: string;
}

const DAY = 86_400_000;
const HOUR = 3_600_000;
const MINUTE = 60_000;

/** Midnight UTC of a calendar date, the year as written: `Date.UTC` reads a
 *  year from 0 to 99 as 1900 plus it, this does not. A month or a day past its
 *  end rolls over into the next, as `Date.UTC` rolls it. */
function utc(y: number, m0: number, d: number): number {
  const at = new Date(0);
  at.setUTCFullYear(y, m0, d);
  return at.getTime();
}

function monthInterval(y: number, m0: number, months: number): [number, number] {
  return [utc(y, m0, 1), utc(y, m0 + months, 1)];
}

/** Monday 00:00 UTC of ISO week `w` in year `y` (Jan 4 is always in ISO week 1). */
function isoWeekStart(y: number, w: number): number {
  const jan4 = utc(y, 0, 4);
  const dow = new Date(jan4).getUTCDay() || 7; // 1..7, Mon..Sun
  return jan4 - (dow - 1) * DAY + (w - 1) * 7 * DAY;
}

/** A date's month and day as the grammar takes them: a month from 01 to 12,
 *  a day from 01 to 31 in any month, a day past the month's end rolling into
 *  the next (`2026-02-30` is 2 March), as the engines' own ISO parser read
 *  it before the grammar read it itself. */
function dayFits(mo: number, d: number): boolean {
  return mo >= 1 && mo <= 12 && d >= 1 && d <= 31;
}

/** A minute-grain token: a date, a time of day to the minute or the second,
 *  and the offset it was written in (`Z`, or `+HH:MM` / `-HH:MM`). */
const MINUTE_TOKEN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:Z|([+-])(\d{2}):(\d{2}))$/;

/** A time of day typed without an offset (`withLocalOffset`). */
const LOCAL_MINUTE_TOKEN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** The instant of a minute-grain token, or null when a field is out of range:
 *  hours 00 to 23 (24:00, the end of a day, with no minutes or seconds), minutes
 *  and seconds 00 to 59, an offset's hours 00 to 23 and its minutes 00 to 59. */
function minuteInstant(m: RegExpMatchArray): number | null {
  // An absent group (no seconds, the `Z` offset) reads as 0.
  const n = (i: number) => Number(m[i] ?? 0);
  const [mo, d, h, mi, s, oh, om] = [n(2), n(3), n(4), n(5), n(6), n(8), n(9)];
  if (!dayFits(mo, d)) return null;
  if (h > 24 || mi > 59 || s > 59 || (h === 24 && (mi > 0 || s > 0))) return null;
  if (oh > 23 || om > 59) return null;
  const sign = m[7] === '-' ? -1 : 1;
  return utc(n(1), mo - 1, d) + h * HOUR + mi * MINUTE + s * 1000 - sign * (oh * 60 + om) * MINUTE;
}

/** Parse one (non-range) grain token into a TimeValue, or null if it matches nothing. */
function parsePoint(token: string): TimeValue | null {
  let m: RegExpMatchArray | null;

  if ((m = token.match(/^(\d{4})$/))) {
    const y = +m[1]!;
    const [s, e] = monthInterval(y, 0, 12);
    return { grain: 'year', startMs: s, endMs: e, canonical: token };
  }
  if ((m = token.match(/^(\d{4})-Q([1-4])$/))) {
    const y = +m[1]!;
    const [s, e] = monthInterval(y, (+m[2]! - 1) * 3, 3);
    return { grain: 'quarter', startMs: s, endMs: e, canonical: token };
  }
  if ((m = token.match(/^(\d{4})-W(\d{2})$/))) {
    const y = +m[1]!;
    const w = +m[2]!;
    if (w < 1 || w > 53) return null;
    const s = isoWeekStart(y, w);
    return { grain: 'week', startMs: s, endMs: s + 7 * DAY, canonical: token };
  }
  if ((m = token.match(/^(\d{4})-(\d{2})$/))) {
    const mo = +m[2]!;
    if (mo < 1 || mo > 12) return null;
    const [s, e] = monthInterval(+m[1]!, mo - 1, 1);
    return { grain: 'month', startMs: s, endMs: e, canonical: token };
  }
  if ((m = token.match(/^(\d{4})-(\d{2})-(\d{2})$/))) {
    const [mo, d] = [+m[2]!, +m[3]!];
    if (!dayFits(mo, d)) return null;
    const t = utc(+m[1]!, mo - 1, d);
    return { grain: 'day', startMs: t, endMs: t + DAY, canonical: token };
  }
  if ((m = token.match(MINUTE_TOKEN))) {
    const t = minuteInstant(m);
    if (t === null) return null;
    return { grain: 'minute', startMs: t, endMs: t, canonical: token };
  }
  return null;
}

/** The refusal for tokens of which one is no grain: a time of day written
 *  without its offset is told so, anything else gets `otherwise`. */
function refusal(tokens: readonly string[], otherwise: Message): Message {
  const local = tokens.find((t) => LOCAL_MINUTE_TOKEN.test(t));
  return local === undefined ? otherwise : m('itemErrors.time.noOffset', { input: local });
}

/**
 * Parse a canonical time string into a `TimeValue`. A `start/end` form is a range
 * (same grain, spanning the first start to the last end). Returns a domain error for
 * anything unparseable, so an invalid target never reaches the projection.
 */
export function parseTimeValue(input: string): Result<TimeValue> {
  const s = input.trim();
  if (s.length === 0) return errOf('invalid_input', m('itemErrors.time.empty'));

  if (s.includes('/')) {
    const [a = '', b = ''] = s.split('/', 2).map((t) => t.trim());
    const from = a ? parsePoint(a) : null;
    const to = b ? parsePoint(b) : null;
    if (!from || !to) {
      return errOf('invalid_input', refusal([a, b], m('itemErrors.time.badRange', { input: s })));
    }
    if (from.grain !== to.grain) return errOf('invalid_input', m('itemErrors.time.mixedGrain'));
    if (to.endMs <= from.startMs)
      return errOf('invalid_input', m('itemErrors.time.endBeforeStart'));
    return ok({
      grain: from.grain,
      startMs: from.startMs,
      endMs: to.endMs,
      canonical: `${from.canonical}/${to.canonical}`,
    });
  }

  const point = parsePoint(s);
  if (!point) {
    return errOf('invalid_input', refusal([s], m('itemErrors.time.unrecognized', { input: s })));
  }
  return ok(point);
}

/**
 * A time value as a person typed it in their own clock, with the offset the
 * device was at for that local time written onto each time of day that has
 * none (`2026-07-15T10:00` in Berlin in summer reads `2026-07-15T10:00+02:00`),
 * so the free-text input never commits one the grammar refuses. `offsetAt`
 * gives the device's offset in minutes east of UTC at a local date and time
 * (the caller asks its own clock; this stays pure). Every other token, and a
 * value with none to complete, comes back as it was.
 */
export function withLocalOffset(
  input: string,
  offsetAt: (local: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  }) => number,
): string {
  return input
    .split('/')
    .map((part) => {
      const token = part.trim();
      const t = token.match(LOCAL_MINUTE_TOKEN);
      if (!t) return part;
      const offset = offsetAt({
        year: +t[1]!,
        month: +t[2]!,
        day: +t[3]!,
        hour: +t[4]!,
        minute: +t[5]!,
        second: t[6] === undefined ? 0 : +t[6],
      });
      const abs = Math.abs(offset);
      const hh = String(Math.floor(abs / 60)).padStart(2, '0');
      const mm = String(abs % 60).padStart(2, '0');
      return `${token}${offset < 0 ? '-' : '+'}${hh}:${mm}`;
    })
    .join('/');
}

/**
 * The calendar face of one edge of a time value: the fields a renderer names
 * it by, at its own grain, with no words and no locale. A day names its date,
 * a week its ISO week, a quarter its quarter; nothing finer than the grain is
 * invented. Day and month fields are UTC, as the whole model is; `ms` is the
 * edge's first instant (a minute-grain value's own instant). The due label
 * (`domain/due.ts`) and the values kit's date (`src/components/values/`) both
 * read it, so the two never name one value two ways.
 */
export type DateFace =
  | { grain: 'minute' | 'day'; ms: number; year: number; month: number; day: number }
  | { grain: 'week'; year: number; week: number }
  | { grain: 'month'; ms: number; year: number; month: number }
  | { grain: 'quarter'; year: number; quarter: number }
  | { grain: 'year'; year: number };

/**
 * The face of one edge of `tv`: `end` (the default) names the last day or
 * period the value covers, which is what a due date means; `start` names the
 * first. A point value has one face on both edges; a range
 * (`2026-W38/2026-W40`) has two. The year of a week is its ISO week-year,
 * read off the token, so `2026-W53` stays in 2026 though it ends in January.
 */
export function dateFace(tv: TimeValue, edge: 'start' | 'end' = 'end'): DateFace {
  const tokens = tv.canonical.split('/');
  const token = (edge === 'end' ? tokens[tokens.length - 1] : tokens[0]) ?? tv.canonical;
  // The canonical form re-parses by construction (it is what parseTimeValue
  // stored); the interval itself stands in should a token ever not.
  const point = parsePoint(token) ?? tv;
  const at = new Date(point.startMs);
  const year = at.getUTCFullYear();
  switch (point.grain) {
    case 'minute':
    case 'day':
      return {
        grain: point.grain,
        ms: point.startMs,
        year,
        month: at.getUTCMonth() + 1,
        day: at.getUTCDate(),
      };
    case 'week': {
      // The ISO week-year and week, off the token; a token that is not a week
      // (never, for a canonical value) falls back to the coarser year.
      const m = token.match(/^(\d{4})-W(\d{2})$/);
      return m ? { grain: 'week', year: +m[1]!, week: +m[2]! } : { grain: 'year', year };
    }
    case 'month':
      return { grain: 'month', ms: point.startMs, year, month: at.getUTCMonth() + 1 };
    case 'quarter':
      return { grain: 'quarter', year, quarter: Math.floor(at.getUTCMonth() / 3) + 1 };
    case 'year':
      return { grain: 'year', year };
  }
}

/**
 * Whether a face names its year: a year always does; any finer face only when
 * its year is not the current one at `now` (UTC), so "18 Sep" this year and
 * "18 Sep 2027" another.
 */
export function faceNamesYear(face: DateFace, now: number): boolean {
  return face.grain === 'year' || face.year !== new Date(now).getUTCFullYear();
}

/**
 * The one fact the time model reports: is `now` past the end of the target interval? A
 * `by 2026-Q3` target is `late` the moment `now` is in Q4. No tolerance, no "due soon",
 * no severity, just the fact. What it means for health requires an authored
 * declaration or jurisdiction rule elsewhere.
 */
export function isLate(tv: TimeValue, now: number): boolean {
  return now >= tv.endMs;
}

/** Whole days from the interval end (positive = past it), a display fact, not a threshold. */
export function lateByDays(tv: TimeValue, now: number): number {
  return Math.floor((now - tv.endMs) / DAY);
}

/**
 * Does `a`'s promised interval end after `b`'s? The one comparison the
 * schedule-conflict fact ships (docs/HEALTH_SIGNALS_V2.md §9: a dependency's
 * declared end after the dependent's own declared end - both promises
 * declared, and they cannot both hold). Grain-honest: intervals compare by
 * their ends, so "2026-08" ends after "2026-08-01" - the month's promise
 * genuinely outlasts the day's. Richer qualifiers (before-start /
 * before-date typing) are deliberately out of scope (v2 §9, the board item
 * "Health signals v1 deferrals").
 */
export function endsAfter(a: TimeValue, b: TimeValue): boolean {
  return a.endMs > b.endMs;
}
